import { create } from 'zustand';

interface ColumnState {
  editable: string[];
  focusedIndex: number | null;
}

enum NavigationDirection {
  Down = 'down',
  Left = 'left',
  Right = 'right',
  Up = 'up',
}

interface MoveFocusResult {
  column: string | null;
  row: number | null;
}

interface TableFocusState {
  activeTableId: string | null;
  columnsByTableId: Record<string, ColumnState>;
  focusedIndexByTableId: Record<string, number | null>;
  focusRowVisualOnly: (tableId: string, index: number) => void;
  lastFocusedIndexByTableId: Record<string, number>;
  /** Tracks the last horizontal navigation direction so disabled cells can skip correctly. */
  lastNavigationDirection: NavigationDirection | null;
  moveFocusDown: (tableId?: string) => number | null;
  moveFocusLeft: (tableId?: string) => MoveFocusResult;
  moveFocusRight: (tableId?: string) => MoveFocusResult;
  moveFocusUp: (tableId?: string) => number | null;
  pendingFocusRestore: boolean;
  removeTable: (tableId: string) => void;
  requestFocusRestore: () => void;
  resetLastFocusedIndexForAllTables: () => void;
  /** False while a focused cell's table is still scrolling it into view. Missing means settled. */
  scrollSettledByTableId: Record<string, boolean>;
  setActiveTableId: (tableId: string | null) => void;
  setEditableColumns: (tableId: string, columns: string[]) => void;
  setFocusedColumn: (tableId: string, columnId: string | null) => void;
  setFocusedIndex: (tableId: string, index: number | null) => void;
  setScrollSettled: (tableId: string, settled: boolean) => void;
  setTotalRows: (tableId: string, count: number) => void;
  suppressCellFocus: boolean;
  totalRowsByTableId: Record<string, number>;
}

/** Resolves the focused column ID from column state, defaulting to the first editable column. */
const getFocusedColumnId = (cols: ColumnState | undefined): string | null => {
  if (!cols || cols.editable.length === 0) {
    return null;
  }
  return cols.editable[cols.focusedIndex ?? 0] ?? null;
};

export { getFocusedColumnId, NavigationDirection };

/**
 * Shared logic for moveFocusLeft and moveFocusRight.
 * Parameterizes column index delta, row wrap target, and edge behavior by direction.
 */
const moveFocusHorizontal = (
  direction: NavigationDirection,
  get: () => TableFocusState,
  set: (fn: (state: TableFocusState) => Partial<TableFocusState>) => void,
  tableId?: string,
): MoveFocusResult => {
  const {
    activeTableId,
    columnsByTableId,
    focusedIndexByTableId,
    totalRowsByTableId,
  } = get();
  const resolvedTableId = tableId ?? activeTableId;
  if (!resolvedTableId) {
    return { column: null, row: null };
  }

  const cols = columnsByTableId[resolvedTableId];
  if (!cols || cols.editable.length === 0) {
    return { column: null, row: null };
  }

  const focusedIndex = focusedIndexByTableId[resolvedTableId] ?? null;
  const totalRows = totalRowsByTableId[resolvedTableId] ?? 0;

  if (totalRows === 0 || focusedIndex === null) {
    return { column: null, row: null };
  }

  const isLeft = direction === NavigationDirection.Left;
  const currentColIndex = cols.focusedIndex ?? 0;
  const canMoveWithinRow = isLeft
    ? currentColIndex > 0
    : currentColIndex < cols.editable.length - 1;

  // Same-row column movement
  if (canMoveWithinRow) {
    const newColIndex = currentColIndex + (isLeft ? -1 : 1);
    set((state) => {
      const currentCols = state.columnsByTableId[resolvedTableId];
      return {
        columnsByTableId: {
          ...state.columnsByTableId,
          [resolvedTableId]: { ...currentCols, focusedIndex: newColIndex },
        },
        lastNavigationDirection: direction,
        suppressCellFocus: false,
      };
    });
    return { column: cols.editable[newColIndex], row: focusedIndex };
  }

  // At boundary — check if we can wrap to an adjacent row
  const canWrapToAdjacentRow = isLeft
    ? focusedIndex > 0
    : focusedIndex < totalRows - 1;

  if (!canWrapToAdjacentRow) {
    return isLeft
      ? { column: null, row: null }
      : { column: cols.editable[currentColIndex], row: focusedIndex };
  }

  const newRowIndex = focusedIndex + (isLeft ? -1 : 1);
  const wrapColIndex = isLeft ? cols.editable.length - 1 : 0;
  set((state) => {
    const currentCols = state.columnsByTableId[resolvedTableId];
    return {
      columnsByTableId: {
        ...state.columnsByTableId,
        [resolvedTableId]: { ...currentCols, focusedIndex: wrapColIndex },
      },
      focusedIndexByTableId: {
        ...state.focusedIndexByTableId,
        [resolvedTableId]: newRowIndex,
      },
      lastFocusedIndexByTableId: {
        ...state.lastFocusedIndexByTableId,
        [resolvedTableId]: newRowIndex,
      },
      lastNavigationDirection: direction,
      suppressCellFocus: false,
    };
  });
  return { column: cols.editable[wrapColIndex], row: newRowIndex };
};

export const useTableFocusStore = create<TableFocusState>((set, get) => ({
  activeTableId: null,
  columnsByTableId: {},
  focusedIndexByTableId: {},

  focusRowVisualOnly: (tableId: string, index: number) => {
    set((state) => {
      const cols = state.columnsByTableId[tableId];
      return {
        ...(cols && {
          columnsByTableId: {
            ...state.columnsByTableId,
            [tableId]: { ...cols, focusedIndex: null },
          },
        }),
        focusedIndexByTableId: {
          ...state.focusedIndexByTableId,
          [tableId]: index,
        },
        lastFocusedIndexByTableId: {
          ...state.lastFocusedIndexByTableId,
          [tableId]: index,
        },
        suppressCellFocus: true,
      };
    });
  },

  lastFocusedIndexByTableId: {},
  lastNavigationDirection: null,

  moveFocusDown: (tableId?: string): number | null => {
    const { activeTableId, focusedIndexByTableId, totalRowsByTableId } = get();
    const resolvedTableId = tableId ?? activeTableId;
    if (!resolvedTableId) {
      return null;
    }
    const focusedIndex = focusedIndexByTableId[resolvedTableId] ?? null;
    const totalRows = totalRowsByTableId[resolvedTableId] ?? 0;
    if (totalRows === 0) {
      return null;
    }

    const newIndex = Math.min(
      focusedIndex === null ? 0 : focusedIndex + 1,
      totalRows - 1,
    );

    set((state) => ({
      focusedIndexByTableId: {
        ...state.focusedIndexByTableId,
        [resolvedTableId]: newIndex,
      },
      lastFocusedIndexByTableId: {
        ...state.lastFocusedIndexByTableId,
        [resolvedTableId]: newIndex,
      },
      lastNavigationDirection: NavigationDirection.Down,
      suppressCellFocus: false,
    }));

    return newIndex;
  },

  moveFocusLeft: (tableId?: string): MoveFocusResult =>
    moveFocusHorizontal(NavigationDirection.Left, get, set, tableId),

  moveFocusRight: (tableId?: string): MoveFocusResult =>
    moveFocusHorizontal(NavigationDirection.Right, get, set, tableId),

  moveFocusUp: (tableId?: string): number | null => {
    const { activeTableId, focusedIndexByTableId, totalRowsByTableId } = get();
    const resolvedTableId = tableId ?? activeTableId;
    if (!resolvedTableId) {
      return null;
    }
    const focusedIndex = focusedIndexByTableId[resolvedTableId] ?? null;
    const totalRows = totalRowsByTableId[resolvedTableId] ?? 0;
    if (totalRows === 0) {
      return null;
    }

    const newIndex = Math.max(focusedIndex === null ? 0 : focusedIndex - 1, 0);

    set((state) => ({
      focusedIndexByTableId: {
        ...state.focusedIndexByTableId,
        [resolvedTableId]: newIndex,
      },
      lastFocusedIndexByTableId: {
        ...state.lastFocusedIndexByTableId,
        [resolvedTableId]: newIndex,
      },
      lastNavigationDirection: NavigationDirection.Up,
      suppressCellFocus: false,
    }));

    return newIndex;
  },

  pendingFocusRestore: false,

  removeTable: (tableId: string) => {
    set((state) => {
      const remainingFocused = Object.fromEntries(
        Object.entries(state.focusedIndexByTableId).filter(
          ([key]) => key !== tableId,
        ),
      );
      const remainingLastFocused = Object.fromEntries(
        Object.entries(state.lastFocusedIndexByTableId).filter(
          ([key]) => key !== tableId,
        ),
      );
      const remainingTotal = Object.fromEntries(
        Object.entries(state.totalRowsByTableId).filter(
          ([key]) => key !== tableId,
        ),
      );
      const remainingColumns = Object.fromEntries(
        Object.entries(state.columnsByTableId).filter(
          ([key]) => key !== tableId,
        ),
      );
      const remainingScrollSettled = Object.fromEntries(
        Object.entries(state.scrollSettledByTableId).filter(
          ([key]) => key !== tableId,
        ),
      );

      return {
        activeTableId:
          state.activeTableId === tableId ? null : state.activeTableId,
        columnsByTableId: remainingColumns,
        focusedIndexByTableId: remainingFocused,
        lastFocusedIndexByTableId: remainingLastFocused,
        scrollSettledByTableId: remainingScrollSettled,
        totalRowsByTableId: remainingTotal,
      };
    });
  },

  requestFocusRestore: () => {
    set({ pendingFocusRestore: true });
  },

  resetLastFocusedIndexForAllTables: () => {
    set((state) => {
      const updates = Object.fromEntries(
        Object.keys(state.lastFocusedIndexByTableId).map((id) => [id, 0]),
      );
      if (Object.keys(updates).length === 0) {
        return state;
      }
      return {
        lastFocusedIndexByTableId: {
          ...state.lastFocusedIndexByTableId,
          ...updates,
        },
      };
    });
  },

  scrollSettledByTableId: {},

  setActiveTableId: (tableId: string | null) => {
    if (get().activeTableId === tableId) {
      return;
    }
    set({ activeTableId: tableId });
  },

  setEditableColumns: (tableId: string, columns: string[]) => {
    const current = get().columnsByTableId[tableId];
    if (
      current &&
      current.editable.length === columns.length &&
      current.editable.every((col, i) => col === columns[i])
    ) {
      return;
    }
    set((state) => ({
      columnsByTableId: {
        ...state.columnsByTableId,
        [tableId]: {
          editable: columns,
          focusedIndex: current?.focusedIndex ?? null,
        },
      },
    }));
  },

  setFocusedColumn: (tableId: string, columnId: string | null) => {
    const cols = get().columnsByTableId[tableId];
    if (!cols) {
      return;
    }
    const newIndex = columnId ? cols.editable.indexOf(columnId) : null;
    const resolvedIndex = newIndex === -1 ? null : newIndex;
    if (cols.focusedIndex === resolvedIndex) {
      return;
    }
    set((state) => {
      const currentCols = state.columnsByTableId[tableId];
      if (!currentCols) {
        return {};
      }
      return {
        columnsByTableId: {
          ...state.columnsByTableId,
          [tableId]: { ...currentCols, focusedIndex: resolvedIndex },
        },
        suppressCellFocus: false,
      };
    });
  },

  setFocusedIndex: (tableId: string, index: number | null) => {
    if (get().focusedIndexByTableId[tableId] === index) {
      return;
    }
    set((state) => {
      const cols = state.columnsByTableId[tableId];
      return {
        ...(cols && {
          columnsByTableId: {
            ...state.columnsByTableId,
            [tableId]: { ...cols, focusedIndex: null },
          },
        }),
        focusedIndexByTableId: {
          ...state.focusedIndexByTableId,
          [tableId]: index,
        },
        ...(index !== null && {
          lastFocusedIndexByTableId: {
            ...state.lastFocusedIndexByTableId,
            [tableId]: index,
          },
        }),
        suppressCellFocus: false,
      };
    });
  },

  setScrollSettled: (tableId: string, settled: boolean) => {
    if ((get().scrollSettledByTableId[tableId] ?? true) === settled) {
      return;
    }
    set((state) => ({
      scrollSettledByTableId: {
        ...state.scrollSettledByTableId,
        [tableId]: settled,
      },
    }));
  },

  setTotalRows: (tableId: string, count: number) => {
    if (get().totalRowsByTableId[tableId] === count) {
      return;
    }
    set((state) => ({
      totalRowsByTableId: {
        ...state.totalRowsByTableId,
        [tableId]: count,
      },
    }));
  },

  suppressCellFocus: false,

  totalRowsByTableId: {},
}));
